import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button';
import { type DataDefinition, DefinitionBox } from '@interxfi/design-system/components/definition-box/definition-box';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  type WrappingLayoutColumnDef,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import { Tooltip } from '@interxfi/design-system/components/tooltip/tooltip';
import { Typography } from '@interxfi/design-system/components/typography';
import { PageTabRail } from '../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import type { ProgramControlDefinitionId } from '../../../../../../../../../features/userauth/program/controls/program-control-sections';
import { ProgramControlSummary } from '../../../../../../../../../features/userauth/program/controls/program-control-summary';
import { ProgramPageHeader } from '../../../../../../../../../features/userauth/program/program-page-header';
import { UpdateProgramControlsDrawer } from '../../../../../../../../../features/userauth/program/update-program-controls-drawer';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';

export const Route = createFileRoute('/_app/_platform/_context-required/program/_program/$programId/_program/configuration/controls')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ProgramConfiguration,
      label: 'Controls',
      order: 3,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ProgramConfiguration>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `program-${params.programId}-configuration-controls`,
        label: 'Configuration',
        to: `/program/${params.programId}/configuration/controls`,
      }),
    }) satisfies AggregateBreadcrumb,
  component: ProgramControlsConfiguration,
});

type ProgramControlDefinition = DataDefinition & { id: ProgramControlDefinitionId };

const controlDefinitions: ProgramControlDefinition[] = [
  {
    id: 'registration.openAccounts',
    title: [
      { id: 'registration', label: 'Registration' },
      { id: 'openAccounts', label: 'Open Accounts' },
    ],
    definition: 'Enables this Program to open customer brokerage accounts.',
  },
  {
    id: 'registration.accountTypes',
    title: [
      { id: 'registration', label: 'Registration' },
      { id: 'accountTypes', label: 'Account Types' },
    ],
    definition: 'Refers to all types of customer brokerage accounts available for this Program.',
  },
  {
    id: 'registration.marginType',
    title: [
      { id: 'registration', label: 'Registration' },
      { id: 'marginType', label: 'Margin Type' },
    ],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Cash</Typography> means the Program can only create Cash accounts—meaning only settled balances are available to
          trade.
        </span>

        <span>
          <Typography weight="bold">Limited Purpose Margin</Typography> means the Program can create Limited Purpose Margin (LPM) and Cash accounts.
          LPM means unsettled proceeds are available to trade.
        </span>

        <span>
          Margin Type can also be set at the account level, but is limited by the Program setting. For example, if Margin Type=Cash at the Program
          level, then Margin Type cannot be set to LPM at the account level.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'registration.states',
    title: [
      { id: 'registration', label: 'Registration' },
      { id: 'states', label: 'State & Territory Registrations' },
    ],
    definition: 'Specifies which States and Territories the Program is registered to operate in.',
  },
  {
    id: 'registration.accountReviewType',
    title: [
      { id: 'registration', label: 'Registration' },
      { id: 'accountReviewType', label: 'Account Review Type' },
    ],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Clearing Automated</Typography> requires evidence of automated Clearing approval.
        </span>

        <span>
          <Typography weight="bold">Clearing Manual</Typography> requires evidence of manual Clearing approval.
        </span>

        <span>
          <Typography weight="bold">Dual Automated</Typography> requires evidence of Program and automated Clearing approvals.
        </span>

        <span>
          <Typography weight="bold">Dual Manual</Typography> requires evidence of Program and manual Clearing approvals.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'registration.specialPurposeAccountReviewType',
    title: [
      { id: 'registration', label: 'Registration' },
      { id: 'specialPurposeAccountReviewType', label: 'Special Purpose Account Review Type' },
    ],
    definition: 'Applies the same review options as Account Review Type, but to special purpose accounts.',
  },
  {
    id: 'registration.cipProgram',
    title: [
      { id: 'registration', label: 'Registration' },
      { id: 'cipProgram', label: 'CIP Program' },
    ],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Disclosed</Typography> requires evidence of identity and OFAC.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'registration.optionalSuitabilityQuestions',
    title: [
      { id: 'registration', label: 'Registration' },
      { id: 'optionalSuitabilityQuestions', label: 'Optional Suitability Questions' },
    ],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Yes</Typography> means the risk and investment profiles are optional when creating or updating a profile or
          account.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'sweeps.allowAccountSweeps',
    title: [
      { id: 'sweeps', label: 'Sweeps' },
      { id: 'allowAccountSweeps', label: 'Allow Account Sweeps' },
    ],
    definition:
      'Enables this Program to sweep customer brokerage accounts. When active, all accounts are set to sweep by default. This can be disabled at the account level.',
  },
  {
    id: 'developers.getApiToken',
    title: [
      { id: 'developers', label: 'Developers' },
      { id: 'getApiToken', label: 'Get API Token' },
    ],
    definition: "Enables this Program's API Keys to authenticate with Interchange and receive bearer tokens.",
  },
  {
    id: 'funding',
    title: [{ id: 'funding', label: 'Funding' }],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Pull</Typography> refers to transferring into Program brokerage accounts from linked customer funding sources.
        </span>

        <span>
          <Typography weight="bold">Push</Typography> is the reverse.
        </span>

        <span>
          <Typography weight="bold">Max Funding Sources</Typography> is the number of linked external bank accounts a single Program brokerage account
          is permitted to have at one time.
        </span>

        <span>All of the above can also be customized at the account level (but is limited by the Program values set here).</span>
      </VerticalStack>
    ),
  },
  {
    id: 'funding.batchFunding',
    title: [
      { id: 'funding', label: 'Funding' },
      { id: 'batchFunding', label: 'Batch Funding' },
    ],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Pull</Typography> enables this Program to move funds into Program brokerage accounts in bulk.
        </span>

        <span>
          <Typography weight="bold">Push</Typography> is the reverse.
        </span>

        <span>
          <Typography weight="bold">Pull/Push Max</Typography> are the maximum dollar amounts permitted in a single batch.
        </span>

        <span>
          <Typography weight="bold">Auto-Approve Batch Pulls/Pushes Under</Typography> are dollar thresholds below which a batch transaction is
          automatically approved without manual review.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'accountNumbers',
    title: [{ id: 'accountNumbers', label: 'Account Numbers' }],
    definition:
      'Enables Program brokerage accounts to receive incoming payments, such as payroll and other direct deposits, pushed in by external senders rather than only pulling funds from linked accounts.',
  },
  {
    id: 'instantDeposit.allowAdvances',
    title: [
      { id: 'instantDeposit', label: 'Instant Deposit' },
      { id: 'allowAdvances', label: 'Allow Advances' },
    ],
    definition: (
      <VerticalStack gap={2}>
        <span>Enables this Program to advance a portion of a pending ACH deposit to an account before it clears.</span>

        <span>
          <Typography weight="bold">Max Advance per Account</Typography> caps how much can be advanced, and can also be set at the account level.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'orders',
    title: [{ id: 'orders', label: 'Orders' }],
    definition: (
      <VerticalStack gap={2}>
        <span>
          <Typography weight="bold">Place Opening Orders</Typography> allows Program brokerage accounts to create new positions by buying shares.
        </span>

        <span>
          <Typography weight="bold">Place Closing Orders</Typography> allows Program brokerage accounts to close existing positions by selling shares.
        </span>

        <span>
          <Typography weight="bold">Place Fractional Orders</Typography> allows trading in fractional shares rather than whole shares. Quantities can
          be specified up to 10 decimal places.
        </span>

        <span>All of the above can also be customized at the account level (but are limited by the Program values set here).</span>

        <span>
          <Typography weight="bold">Charge Commission</Typography> enables this Program to apply a commission to executed trades.
        </span>
      </VerticalStack>
    ),
  },
  {
    id: 'batchOrder',
    title: [{ id: 'batchOrder', label: 'Batch Orders' }],
    definition: 'Enables this Program to place batch orders on US equities.',
  },
  {
    id: 'books.tradeUnsettledCash',
    title: [
      { id: 'books', label: 'Books' },
      { id: 'tradeUnsettledCash', label: 'Trade Unsettled Cash' },
    ],
    definition: 'Controls whether unsettled proceeds are available for transactions in LPM accounts.',
  },
];

const placeholderDefinitions: DataDefinition[] = [
  {
    id: 'placeholder',
    definition: 'Expand a section to view control definitions',
  },
];

const rightColumn = { width: { type: 'fixed', width: 320 }, overflow: 'hidden' } satisfies Omit<WrappingLayoutColumnDef, 'render'>;

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load program controls', "view this program's control data");

function ProgramControlsConfiguration() {
  const { programId } = Route.useParams();
  const { data, isPending: showPending, error } = useGetProgram({ programId });
  useErrorHandler(error, errorMessageGetter);
  const { activeTenantIsPlatform } = useActiveRealmAPIContext();
  const [openSections, setOpenSections] = useState<string[]>([]);
  const visibleDefinitions = useMemo(() => {
    const sections = new Set(openSections);
    const filtered = controlDefinitions.filter((definition) => sections.has(definition.id.split('.')[0]));

    return filtered.length ? filtered : placeholderDefinitions;
  }, [openSections]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <ProgramPageHeader programId={programId} withEntityMenu />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <PageTabRail
          actions={
            activeTenantIsPlatform ? (
              <RequiresAccess write>
                <UpdateProgramControlsDrawer programId={programId} trigger={<Button size="small">Edit</Button>} />
              </RequiresAccess>
            ) : (
              <Tooltip
                activator={
                  <Button disabled size="small">
                    Edit
                  </Button>
                }
                maxWidth={200}
              >
                Please contact the Interchange Team to make changes to your program's controls.
              </Tooltip>
            )
          }
          group={TabbedNavigationGroup.ProgramConfiguration}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: (
              <ProgramControlSummary
                controls={data?.program?.data?.controls}
                isPending={showPending}
                onOpenSectionsChange={setOpenSections}
                openSections={openSections}
                programId={programId}
              />
            ),
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...rightColumn,
            render: (isWrapping) => (isWrapping ? null : <DefinitionBox definitions={visibleDefinitions} />),
          }}
          wrap="forward"
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
